'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Instructor {
  id: string;
  user_id: string;
  full_name: string;
  title: string;
  total_courses: number;
  total_students: number;
  rating: number;
  avatar_url?: string;
  initials: string;
  color: string;
}

const colorPalette = [
  'bg-purple-100 text-purple-700',
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
  'bg-red-100 text-red-700',
];

export default function InstructorsSection() {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInstructors();
  }, []);

  const loadInstructors = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('lecturers')
        .select(`
          id, user_id, title, total_courses, total_students, average_rating,
          user_profiles(full_name, avatar_url)
        `)
        .eq('is_approved', true)
        .order('total_students', { ascending: false })
        .limit(4);

      if (error) throw error;

      const mapped: Instructor[] = (data || []).map((inst: any, idx: number) => {
        const name = inst.user_profiles?.full_name || 'Instructor';
        const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 3);
        return {
          id: inst.id,
          user_id: inst.user_id,
          full_name: name,
          title: inst.title || 'Course Instructor',
          total_courses: inst.total_courses || 0,
          total_students: inst.total_students || 0,
          rating: parseFloat(inst.average_rating) || 4.5,
          avatar_url: inst.user_profiles?.avatar_url,
          initials,
          color: colorPalette[idx % colorPalette.length],
        };
      });

      setInstructors(mapped);
    } catch (err) {
      console.log('Failed to load instructors:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="instructors" className="py-16 bg-white">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-primary mb-2 uppercase tracking-widest">Expert Instructors</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-foreground">
            Learn from <span className="text-gradient">the best minds</span>
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Every instructor is vetted for expertise, teaching quality, and real-world experience before joining Nerdlora.
          </p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4].map((i) => (
              <div key={i} className="bg-background rounded-2xl p-6 border border-border shadow-card animate-pulse">
                <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4" />
                <div className="h-4 bg-muted rounded mx-auto w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded mx-auto w-1/2" />
              </div>
            ))}
          </div>
        ) : instructors.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="UserGroupIcon" size={48} className="mx-auto mb-4 text-muted" />
            <p className="text-lg font-semibold text-foreground mb-2">Instructors Coming Soon</p>
            <p className="text-sm">Our expert instructors are being onboarded. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {instructors.map((inst) => (
              <div key={inst.id} className="bg-background rounded-2xl p-6 border border-border shadow-card hover:shadow-card-hover transition-all duration-200 text-center group">
                <div className={`w-16 h-16 rounded-full ${inst.color} flex items-center justify-center mx-auto mb-4 text-xl font-extrabold group-hover:scale-110 transition-transform`}>
                  {inst.initials}
                </div>
                <h3 className="font-bold text-foreground mb-1">{inst.full_name}</h3>
                <p className="text-xs text-muted-foreground mb-3 leading-snug">{inst.title}</p>
                <div className="flex items-center justify-center gap-1 mb-3">
                  {[1,2,3,4,5].map((s) => (
                    <Icon key={`${inst.id}-star-${s}`} name="StarIcon" size={12} className={s <= Math.floor(inst.rating) ? 'text-warning' : 'text-muted'} variant="solid" />
                  ))}
                  <span className="text-xs font-bold text-warning ml-1">{inst.rating.toFixed(1)}</span>
                </div>
                <div className="flex justify-center gap-4 text-xs text-muted-foreground">
                  <span className="flex flex-col items-center">
                    <span className="font-bold text-foreground tabular-nums">{inst.total_courses}</span>
                    Courses
                  </span>
                  <span className="flex flex-col items-center">
                    <span className="font-bold text-foreground tabular-nums">{inst.total_students.toLocaleString()}</span>
                    Students
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-8">
          <Link
            href="/sign-up-login-screen"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-primary border border-primary/30 rounded-xl hover:bg-secondary transition-all"
          >
            Become an Instructor
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}